import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Stack, Typography } from "@mui/material";
import Datatable from "components/Datatable/Datatable";
import { useTranslation } from "react-i18next";
import moment from "moment";
import { spinning } from "../utils";
import ActionsCell from "./components/ActionsCell";
import FilesCell from "./components/FilesCell";
import PreviewDialog from "./components/PreviewDialog";
import UploadDialog from "./components/UploadDialog";
import SimpleDialog from "components/SimpleDialog/SimpleDialog";
import s from "./MerchantDocumentUpload.module.scss";
import {
  deleteMerchantDocument,
  queryMerchantDocuments,
  requestMerchantDocumentUpload,
  uploadMerchantDocument,
} from "../model";
import useActionSnackBar from "utils/useActionSnackBar/useActionSnackBar";

const DOCUMENT_TAB_LOCALE = "page.merchants.merchantDetail.documentUpload.";
const ACCEPT_ATTR = ".jpg,.jpeg,.png,.pdf";
const MAX_FILE_SIZE_MB = 5;
const MAX_FILES_PER_CATEGORY = 10;
const INVALID_FILENAME_PATTERN = /[\\/:*?"<>|]/;
const INVALID_FILENAME_CHARS_DISPLAY = '\\ / : * ? " < > |';

// Sort documents by created date (newest first)
const sortDocumentsByCreatedDate = (documents) => {
  return [...documents].sort((a, b) => {
    const dateA = a.created ? moment(a.created).valueOf() : 0;
    const dateB = b.created ? moment(b.created).valueOf() : 0;
    return dateB - dateA;
  });
};
const DOCUMENT_DEFINITIONS = [
  {
    key: "BR",
    english: "Business Registration (BR)",
    chinese: "商業登記證 (BR)",
  },
  {
    key: "CI",
    english: "Certificate of Incorporation (CI)",
    chinese: "公司註冊證書 (CI)",
  },
  {
    key: "EI",
    english: "Electronic Extract of Information (EI)",
    chinese: "電子資料摘錄 (EI)",
  },
  {
    key: "OWNER_ID",
    english: "Business Owner ID(s)",
    chinese: "業務擁有人身份證明文件",
  },
  {
    key: "BANK_STATEMENT",
    english: "Bank Statement / Bank Book / Card / Cheque",
    chinese: "銀行月結單 / 銀行存摺 / 銀行卡 / 支票 (最近3個月內)",
  },
  {
    key: "STORE_PHOTO",
    english: "Pictures of Store Front",
    chinese: "店舖正面照片",
  },
  {
    key: "ANNUAL_RETURN",
    english: "Annual Return / Incorporation Form",
    chinese: "周年申報表 (NAR1) / 公司成立表格 (NNC1)",
  },
  {
    key: "SIGNED_APPLICATION",
    english: "Signed Application Form",
    chinese: "已簽署的申請表",
  },
  {
    key: "OTHERS",
    english: "Others",
    chinese: "其他",
  },
];

const MerchantDocumentUpload = ({ merchantId }) => {
  const { t } = useTranslation();
  const { actionSnackBar } = useActionSnackBar(DOCUMENT_TAB_LOCALE);
  const [documentMap, setDocumentMap] = useState({});
  const [loading, setLoading] = useState(false);
  const [uploadingKey, setUploadingKey] = useState();
  const [deletingFileId, setDeletingFileId] = useState();
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [filePendingDeletion, setFilePendingDeletion] = useState(null);
  const [isUploadDialogOpen, setIsUploadDialogOpen] = useState(false);
  const [pendingUpload, setPendingUpload] = useState(null);
  const [isPreviewDialogOpen, setIsPreviewDialogOpen] = useState(false);
  const [previewContext, setPreviewContext] = useState(null);
  const [draggingCategory, setDraggingCategory] = useState(null);
  const fileInputRefs = useRef({});

  const openPreview = useCallback((categoryKey, index) => {
    if (!categoryKey || index == null) return;
    setPreviewContext({ categoryKey, index });
    setIsPreviewDialogOpen(true);
  }, []);

  const resetInput = useCallback((categoryKey) => {
    const node = fileInputRefs.current?.[categoryKey];
    if (node) {
      node.value = "";
    }
  }, []);

  const disposePreviewUrls = useCallback((urls = []) => {
    if (!Array.isArray(urls)) {
      return;
    }
    urls.forEach((url) => {
      if (url) {
        URL.revokeObjectURL(url);
      }
    });
  }, []);

  const buildPreviewUrls = useCallback((documents = []) => {
    return documents.map((document) => {
      // Check both contentType and type properties (File objects have type)
      const contentType = document?.contentType || document?.type;
      if (!contentType) {
        return null;
      }
      if (
        contentType.startsWith("image/") ||
        contentType === "application/pdf"
      ) {
        if (document instanceof File || document instanceof Blob) {
          return URL.createObjectURL(document);
        }
        return null;
      }
      return null;
    });
  }, []);

  const normaliseDocuments = useCallback((list = []) => {
    const grouped = list.reduce((acc, document) => {
      const category = document?.category;
      if (!category) return acc;
      
      if (!acc[category]) {
        acc[category] = [];
      }
      acc[category].push({
        ...document,
        id: document?.path,
        // Normalize URL field - support multiple possible field names
        url: document?.url || document?.downloadUrl,
      });
      
      return acc;
    }, {});

    // Sort each category by created date (newest first)
    Object.keys(grouped).forEach((key) => {
      grouped[key] = sortDocumentsByCreatedDate(grouped[key]);
    });
    
    return grouped;
  }, []);

  const fetchDocuments = useCallback(async () => {
    if (!merchantId) {
      setDocumentMap({});
      return;
    }
    setLoading(true);
    try {
      const response = await queryMerchantDocuments(merchantId);
      const responseBody = response?.data;
      if (
        responseBody?.responseCode === "0000" &&
        Array.isArray(responseBody?.data)
      ) {
        setDocumentMap(normaliseDocuments(responseBody.data));
      } else {
        throw new Error(responseBody?.responseMessage || "Fetch failed");
      }
    } catch (error) {
      actionSnackBar("fetch", "failed");
    } finally {
      setLoading(false);
    }
  }, [merchantId, normaliseDocuments]);

  useEffect(() => {
    fetchDocuments();
  }, [fetchDocuments]);

  useEffect(() => {
    return () => {
      if (pendingUpload?.previewUrls?.length) {
        disposePreviewUrls(pendingUpload.previewUrls);
      }
    };
  }, [disposePreviewUrls, pendingUpload]);

  const handleUpload = useCallback(
    async (categoryKey, documentList) => {
      const documents = Array.from(documentList || []);
      if (!documents.length || !merchantId) {
        resetInput(categoryKey);
        return;
      }
      setUploadingKey(categoryKey);
      const documentsPayload = documents.map((document) => {
        return {
          name: document.name,
          category: categoryKey,
          contentType: document.contentType,
          size: document.size,
        };
      });
      try {
        const presignResponse = await requestMerchantDocumentUpload({
          merchantId,
          documents: documentsPayload,
        });

        const presignBody = presignResponse?.data;
        if (
          presignBody?.responseCode !== "0000" ||
          !Array.isArray(presignBody?.data) ||
          presignBody.data.length !== documents.length
        ) {
          throw new Error(
            presignBody?.responseMessage ||
              presignBody?.message ||
              t(`${DOCUMENT_TAB_LOCALE}messages.uploadFailed`),
          );
        }

        await Promise.all(
          presignBody.data.map(async (uploadTarget, index) => {
            const document = documents[index];
            documentsPayload[index].path = uploadTarget.key;
            const response = await fetch(uploadTarget.url, {
              method: "PUT",
              headers: {
                "Content-Type": document.contentType || "application/octet-stream",
              },
              body: document,
            });
            if (!response.ok) {
              throw new Error("Failed to upload document content.");
            }
          }),
        );

        const response = await uploadMerchantDocument({
          merchantId,
          documents: documentsPayload,
        });
        const responseBody = response?.data;
        if (responseBody?.responseCode !== "0000") {
          throw new Error(
            responseBody?.responseMessage ||
              responseBody?.message ||
              t(`${DOCUMENT_TAB_LOCALE}messages.uploadFailed`),
          );
        }
        
        // If API returns the created documents, add them directly to the category
        // Otherwise, fetch all documents to get URLs and other server-generated fields
        if (Array.isArray(responseBody?.data) && responseBody.data.length > 0) {
          const normalizedNewDocs = normaliseDocuments(responseBody.data);
          setDocumentMap((prevMap) => {
            const newMap = { ...prevMap };
            if (normalizedNewDocs[categoryKey]) {
              // Merge and sort: both arrays are already sorted by created date
              const combined = [...normalizedNewDocs[categoryKey]];
              // Re-sort merged array by created date (newest first)
              newMap[categoryKey] = sortDocumentsByCreatedDate(combined);
            }
            return newMap;
          });
        } else {
          // Fallback: fetch all documents if response doesn't include document data
          await fetchDocuments();
        }
        
        actionSnackBar("upload", "success");
      } catch (error) {
        console.error("Upload merchant document error:", error);
        actionSnackBar("upload", "failed", {
          message: error?.message || t(`${DOCUMENT_TAB_LOCALE}messages.uploadFailed`),
        });
      } finally {
        resetInput(categoryKey);
        setUploadingKey(undefined);
      }
    },
    [
      fetchDocuments,
      merchantId,
      normaliseDocuments,
      requestMerchantDocumentUpload,
      resetInput,
      uploadMerchantDocument,
      t,
      actionSnackBar,
    ],
  );

  const requestUploadConfirmation = useCallback(
    (categoryKey, documentList) => {
      const fileList = Array.from(documentList || []);
      if (!fileList.length || !merchantId) {
        resetInput(categoryKey);
        return;
      }
      
      // Ensure contentType is set on documents before building preview URLs
      const documents = fileList.map((doc) => {
        console.log("doc", doc);
        if (doc instanceof File || doc instanceof Blob) {
          doc.contentType = doc.contentType || doc.type;
          return doc;
        }
        return {
          ...doc,
          contentType: doc.contentType || doc.type,
        };
      });
      
      // Build preview URLs after contentType is set
      const previewUrls = buildPreviewUrls(documents);
      
      setPendingUpload((prev) => {
        if (prev?.previewUrls?.length) {
          disposePreviewUrls(prev.previewUrls);
        }
        return {
          categoryKey,
          documents,
          previewUrls,
        };
      });
      setIsUploadDialogOpen(true);
    },
    [buildPreviewUrls, disposePreviewUrls, merchantId, resetInput],
  );

  const handleUploadDialogClose = useCallback(() => {
    setPendingUpload((prev) => {
      if (prev?.categoryKey) {
        resetInput(prev.categoryKey);
      }
      if (prev?.previewUrls?.length) {
        disposePreviewUrls(prev.previewUrls);
      }
      return null;
    });
    setIsUploadDialogOpen(false);
  }, [disposePreviewUrls, resetInput]);

  const handleRemovePendingDocument = useCallback(
    (documentIndex) => {
      setPendingUpload((prev) => {
        if (!prev) {
          return prev;
        }
        const nextDocuments = prev.documents.filter(
          (_, idx) => idx !== documentIndex,
        );
        const nextPreviewUrls = prev.previewUrls?.filter(
          (_, idx) => idx !== documentIndex,
        );
        const removedPreview = prev.previewUrls?.[documentIndex];
        if (removedPreview) {
          URL.revokeObjectURL(removedPreview);
        }
        if (!nextDocuments.length) {
          resetInput(prev.categoryKey);
          setIsUploadDialogOpen(false);
          disposePreviewUrls(nextPreviewUrls);
          return null;
        }
        return {
          ...prev,
          documents: nextDocuments,
          previewUrls: nextPreviewUrls,
        };
      });
    },
    [disposePreviewUrls, resetInput],
  );

  const pendingUploadValidation = useMemo(() => {
    if (!pendingUpload?.documents?.length) {
      return null;
    }
    const existingList = documentMap[pendingUpload.categoryKey] || [];
    const existingCount = existingList.length;
    const selectedCount = pendingUpload.documents.length;
    const totalAfterUpload = existingCount + selectedCount;
    const remainingSlots = Math.max(MAX_FILES_PER_CATEGORY - existingCount, 0);
    const existingNames = new Set(
      existingList
        .map((document) => document?.name?.toLowerCase())
        .filter(Boolean),
    );
    const seenNames = new Set(existingNames);
    const documents = pendingUpload.documents.map((document, index) => {
      const normalizedName = document?.name?.toLowerCase();
      const duplicate = normalizedName ? seenNames.has(normalizedName) : false;
      if (normalizedName) {
        seenNames.add(normalizedName);
      }
      const oversize =
        typeof document?.size === "number" &&
        document.size > MAX_FILE_SIZE_MB * 1024 * 1024;
      const invalidName = document?.name && INVALID_FILENAME_PATTERN.test(document?.name);
      return {
        document,
        duplicate,
        oversize,
        invalidName,
        previewUrl: pendingUpload.previewUrls?.[index] ?? null,
      };
    });
    const duplicateEntry = documents.find((item) => item.duplicate);
    const oversizeEntry = documents.find((item) => item.oversize);
    const invalidNameEntry = documents.find((item) => item.invalidName);
    const exceedsLimit = totalAfterUpload > MAX_FILES_PER_CATEGORY;
    return {
      documents,
      firstDuplicateName: duplicateEntry?.document?.name,
      firstOversizeName: oversizeEntry?.document?.name,
      firstInvalidName: invalidNameEntry?.document?.name,
      existingCount,
      selectedCount,
      totalAfterUpload,
      remainingSlots,
      exceedsLimit,
      overLimitBy: exceedsLimit ? totalAfterUpload - MAX_FILES_PER_CATEGORY : 0,
      hasInvalidName: !!invalidNameEntry,
    };
  }, [documentMap, pendingUpload]);

  const handleDragOver = useCallback(
    (event, categoryKey) => {
      event.preventDefault();
      if (draggingCategory !== categoryKey) {
        setDraggingCategory(categoryKey);
      }
    },
    [draggingCategory],
  );

  const handleDragLeave = useCallback(
    (event) => {
      event.preventDefault();
      if (draggingCategory) {
        setDraggingCategory(null);
      }
    },
    [draggingCategory],
  );

  const handleDrop = useCallback(
    (event, categoryKey) => {
      event.preventDefault();
      setDraggingCategory(null);
      const files = event.dataTransfer?.files;
      if (!merchantId) {
        return;
      }
      if (files?.length) {
        requestUploadConfirmation(categoryKey, files);
      }
    },
    [merchantId, requestUploadConfirmation],
  );

  const handleDelete = useCallback(
    async (file) => {
      if (!merchantId) return;
      const documentId = file?.id || file?.documentId;
      if (!documentId) return;
      
      // Extract category from file path or use file.category
      const category = file?.category || file?.path?.split("/")?.[1];
      if (!category) return;
      
      setDeletingFileId(documentId);
      
      try {
        const response = await deleteMerchantDocument(file?.path);
        const responseBody = response?.data;
        if (responseBody?.responseCode !== "0000") {
          throw new Error(
            responseBody?.responseMessage ||
              responseBody?.message ||
              t(`${DOCUMENT_TAB_LOCALE}messages.deleteFailed`),
          );
        }
        
        // Only remove file after successful deletion
        setDocumentMap((prevMap) => {
          const newMap = { ...prevMap };
          if (newMap[category]) {
            newMap[category] = newMap[category].filter(
              (doc) => (doc.id || doc.documentId) !== documentId,
            );
          }
          return newMap;
        });
        
        actionSnackBar("delete", "success");
      } catch (error) {
        console.error("Delete merchant document error:", error);
        actionSnackBar("delete", "failed", {
          message: error?.message || t(`${DOCUMENT_TAB_LOCALE}messages.deleteFailed`),
        });
      } finally {
        setDeletingFileId(undefined);
      }
    },
    [merchantId, t, actionSnackBar],
  );

  const dataSource = useMemo(
    () =>
      DOCUMENT_DEFINITIONS.map((definition) => ({
        id: definition.key,
        english: definition.english,
        chinese: definition.chinese,
        files: documentMap[definition.key] || [],
      })),
    [documentMap, t],
  );

  const pendingCategoryLabel = useMemo(() => {
    if (!pendingUpload?.categoryKey) {
      return "";
    }
    return (
      DOCUMENT_DEFINITIONS.find(
        (definition) => definition.key === pendingUpload.categoryKey,
      )?.english || pendingUpload.categoryKey
    );
  }, [pendingUpload]);

  const pendingUploadPreviewDocuments = useMemo(() => {
    if (!pendingUpload?.documents?.length) {
      return [];
    }
    if (pendingUploadValidation?.documents?.length) {
      return pendingUploadValidation.documents;
    }
    return pendingUpload.documents.map((document, index) => ({
      document,
      duplicate: false,
      oversize: false,
      invalidName: false,
      previewUrl: pendingUpload.previewUrls?.[index] ?? null,
    }));
  }, [pendingUpload, pendingUploadValidation]);

  const isPendingUploadUploading =
    pendingUpload && uploadingKey === pendingUpload.categoryKey;

  const previewDocuments = useMemo(() => {
    if (!previewContext?.categoryKey) {
      return [];
    }
    return documentMap[previewContext.categoryKey] || [];
  }, [documentMap, previewContext]);

  const previewIndex = previewContext?.index ?? 0;
  const previewDocument =
    previewIndex >= 0 && previewIndex < previewDocuments.length
      ? previewDocuments[previewIndex]
      : null;

  const confirmPendingUpload = useCallback(async () => {
    if (!pendingUpload) {
      return;
    }
    if (pendingUploadValidation?.exceedsLimit) {
      actionSnackBar("upload", "info", {
        message: t(`${DOCUMENT_TAB_LOCALE}messages.limitExceeded`, {
          max: MAX_FILES_PER_CATEGORY,
        }),
      });
      return;
    }
    const oversizeFileName = pendingUploadValidation?.firstOversizeName;
    if (oversizeFileName) {
      actionSnackBar("upload", "info", {
        message: t(`${DOCUMENT_TAB_LOCALE}messages.invalidSize`, {
          size: MAX_FILE_SIZE_MB,
        }),
      });
      return;
    }
    const duplicateName = pendingUploadValidation?.firstDuplicateName;
    if (duplicateName) {
      actionSnackBar("upload", "info", {
        message: t(`${DOCUMENT_TAB_LOCALE}messages.duplicateName`, {
          fileName: duplicateName,
        }),
      });
      return;
    }
    const invalidName = pendingUploadValidation?.firstInvalidName;
    if (invalidName) {
      actionSnackBar("upload", "info", {
        message: t(`${DOCUMENT_TAB_LOCALE}messages.invalidName`, {
          chars: INVALID_FILENAME_CHARS_DISPLAY,
        }),
      });
      return;
    }
    await handleUpload(pendingUpload.categoryKey, pendingUpload.documents);
    setPendingUpload((prev) => {
      if (prev?.previewUrls?.length) {
        disposePreviewUrls(prev.previewUrls);
      }
      return null;
    });
    setIsUploadDialogOpen(false);
  }, [
    disposePreviewUrls,
    handleUpload,
    pendingUpload,
    pendingUploadValidation,
    t,
    actionSnackBar,
  ]);

  const handlePreviewClose = useCallback(() => {
    setIsPreviewDialogOpen(false);
    setPreviewContext(null);
  }, []);

  const handlePreviewJump = useCallback((targetIndex) => {
    setPreviewContext((prev) => {
      if (!prev?.categoryKey) {
        return prev;
      }
      return {
        ...prev,
        index: targetIndex,
      };
    });
  }, []);

  const handlePreviewStep = useCallback(
    (delta) => {
      setPreviewContext((prev) => {
        if (!prev?.categoryKey) {
          return prev;
        }
        const list = documentMap[prev.categoryKey] || [];
        if (!list.length) {
          return prev;
        }
        const nextIndex = Math.min(
          Math.max((prev.index ?? 0) + delta, 0),
          list.length - 1,
        );
        return {
          ...prev,
          index: nextIndex,
        };
      });
    },
    [documentMap],
  );

  const handlePreviewPrev = useCallback(
    () => handlePreviewStep(-1),
    [handlePreviewStep],
  );

  const handlePreviewNext = useCallback(
    () => handlePreviewStep(1),
    [handlePreviewStep],
  );

  const handleDeleteDialogClose = useCallback(() => {
    setIsDeleteDialogOpen(false);
    setFilePendingDeletion(null);
  }, []);

  const confirmDelete = useCallback(async () => {
    if (!filePendingDeletion) {
      return;
    }
    const fileToDelete = filePendingDeletion;
    await handleDelete(fileToDelete);
    handleDeleteDialogClose();
  }, [filePendingDeletion, handleDelete, handleDeleteDialogClose]);

  const handleDeleteRequest = useCallback((file) => {
                  setFilePendingDeletion(file);
                  setIsDeleteDialogOpen(true);
  }, []);

  const renderFilesCell = useCallback(
    (row) => (
      <FilesCell
        row={row}
        deletingFileId={deletingFileId}
        onDeleteRequest={handleDeleteRequest}
        onOpenPreview={openPreview}
      />
    ),
    [deletingFileId, handleDeleteRequest, openPreview],
  );

  const renderActionsCell = useCallback(
    (row) => (
      <ActionsCell
        rowId={row.id}
        draggingCategory={draggingCategory}
        merchantId={merchantId}
        uploadingKey={uploadingKey}
        fileInputRefs={fileInputRefs}
        acceptAttr={ACCEPT_ATTR}
        uploadLabel={t(`${DOCUMENT_TAB_LOCALE}buttons.upload`)}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onRequestUpload={requestUploadConfirmation}
      />
    ),
    [
      ACCEPT_ATTR,
      draggingCategory,
      handleDragLeave,
      handleDragOver,
      handleDrop,
      merchantId,
      requestUploadConfirmation,
      t,
      uploadingKey,
    ],
  );

  const columns = useMemo(
    () => [
      {
        title: t(`${DOCUMENT_TAB_LOCALE}columns.english`),
        key: "english",
        render: (row) => (
          <Stack direction="column" gap={0.5}>
            <Typography variant="body2">{row.english}</Typography>
            <Typography variant="caption" sx={{ color: "text.secondary" }}>
              {row.chinese}
            </Typography>
          </Stack>
        ),
      },
      {
        title: t(`${DOCUMENT_TAB_LOCALE}columns.files`),
        key: "files",
        render: (row) => renderFilesCell(row),
      },
      {
        title: t(`${DOCUMENT_TAB_LOCALE}columns.actions`),
        key: "actions",
        render: (row) => renderActionsCell(row),
      },
    ],
    [
      draggingCategory,
      handleDragLeave,
      handleDragOver,
      handleDrop,
      renderActionsCell,
      renderFilesCell,
      t,
    ],
  );

  return (
    <Stack className={s.container} gap={3}>
      {loading ? (
        <Stack alignItems="center">{spinning()}</Stack>
      ) : (
        <>
          <div className={s.datatableWrapper}>
            <Datatable
              columns={columns}
              datasource={dataSource}
              sorting={null}
              defaultSortingOrder={["", ""]}
            />
          </div>
          <Stack className={s.footnotes}>
            <Typography variant="caption">
              {t(`${DOCUMENT_TAB_LOCALE}footnotes.types`)}
            </Typography>
            <Typography variant="caption">
              {t(`${DOCUMENT_TAB_LOCALE}footnotes.size`, {
                size: MAX_FILE_SIZE_MB,
              })}
            </Typography>
            <Typography variant="caption">
              {t(`${DOCUMENT_TAB_LOCALE}footnotes.count`, {
                count: MAX_FILES_PER_CATEGORY,
              })}
            </Typography>
          </Stack>
        </>
      )}
      <UploadDialog
        open={isUploadDialogOpen}
        pendingUpload={pendingUpload}
        pendingUploadValidation={pendingUploadValidation}
        pendingCategoryLabel={pendingCategoryLabel}
        isPendingUploadUploading={!!isPendingUploadUploading}
        pendingUploadPreviewDocuments={pendingUploadPreviewDocuments}
        onClose={handleUploadDialogClose}
        onConfirm={confirmPendingUpload}
        onRemovePendingDocument={handleRemovePendingDocument}
        t={t}
        localePrefix={DOCUMENT_TAB_LOCALE}
        maxFileSizeMb={MAX_FILE_SIZE_MB}
        maxFilesPerCategory={MAX_FILES_PER_CATEGORY}
        invalidFileNameChars={INVALID_FILENAME_CHARS_DISPLAY}
      />
      <PreviewDialog
        open={isPreviewDialogOpen}
        previewDocument={previewDocument}
        previewDocuments={previewDocuments}
        previewIndex={previewIndex}
        onPrev={handlePreviewPrev}
        onNext={handlePreviewNext}
        disablePrev={previewIndex <= 0}
        disableNext={previewIndex >= (previewDocuments.length || 1) - 1}
        onClose={handlePreviewClose}
        onJump={handlePreviewJump}
        t={t}
        localePrefix={DOCUMENT_TAB_LOCALE}
      />
      <SimpleDialog
        open={isDeleteDialogOpen}
        dialogOncloseFn={handleDeleteDialogClose}
        title={t(`${DOCUMENT_TAB_LOCALE}dialog.deleteTitle`)}
        content={t(`${DOCUMENT_TAB_LOCALE}dialog.deleteContent`, {
          fileName: filePendingDeletion?.name ?? "",
        })}
        positiveAction={t(`${DOCUMENT_TAB_LOCALE}dialog.confirm`)}
        negativeAction={t(`${DOCUMENT_TAB_LOCALE}dialog.cancel`)}
        positiveActionLoading={!!deletingFileId}
        negativeActionDisabled={!!deletingFileId}
        confirmFn={confirmDelete}
      />
    </Stack>
  );
};

export default React.memo(MerchantDocumentUpload);
